// src/app/core/mainPage/components/NewsModal.tsx
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import type { NewsItem } from '@/app/core/mainPage/prompt/shared/types';

interface NewsModalProps {
  news: NewsItem | null;
  onClose: () => void;
}

export const NewsModal = ({ news, onClose }: NewsModalProps) => {
  useEffect(() => {
    if (news) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [news]);

  if (!news) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      >
        <motion.div
          key={news.id}
          style={{
            maxWidth: '320px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            backgroundColor: 'var(--card)',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
          onClick={(e) => e.stopPropagation()}
        >
          {news.imageUrl && (
            <img
              src={news.imageUrl}
              alt={news.title}
              style={{
                width: '100%',
                height: '176px',
                objectFit: 'cover',
                borderRadius: '12px',
                marginBottom: '16px',
              }}
            />
          )}
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--foreground)', marginBottom: '8px' }}>
            {news.title}
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: 'var(--muted-foreground)',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
            }}
          >
            {news.fullText}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};